"use client";

import React, { createContext, useContext } from "react";
import { useABTestTrackingAfter } from "@/hooks/use-ab-tests";
import { ExperimentClient } from "@amplitude/experiment-js-client";
import { User } from "@/providers/amplitude-provider/amplitude-provider.types";

const AB_TEST_NAME = "undo-cancel-notification-1" as const;

const VARIANTS = ["toast", "modal"] as const;
type NotificationVariant = (typeof VARIANTS)[number];

const checkVariant = (input: string): NotificationVariant | undefined =>
  VARIANTS.includes(input as NotificationVariant)
    ? (input as NotificationVariant)
    : undefined;

interface ABTestUndoCancelState {
  variant?: NotificationVariant;
  trackABConversion: () => void;
  fetchExperiment: (
    experiment: ExperimentClient,
    user: User,
  ) => Promise<string | null>;
  user?: User | null;
  experiment?: ExperimentClient;
}

const ABTestUndoCancelContext = createContext<ABTestUndoCancelState | null>(
  null,
);

export const useABTestUndoCancelContext = (): ABTestUndoCancelState => {
  const context = useContext(ABTestUndoCancelContext);
  if (!context) {
    throw new Error(
      "useABTestUndoCancelContext must be used within a ABTestUndoCancelProvider",
    );
  }
  return context;
};

const ABTestUndoCancelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { variant, trackConversion, experiment, user, fetchExperiment } =
    useABTestTrackingAfter(AB_TEST_NAME);
  const trackABConversion = () => {
    trackConversion("ab-test-conv-undo-cancel-1-on-click-btn", {});
  };

  return (
    <ABTestUndoCancelContext.Provider
      value={{
        variant: variant === null ? undefined : checkVariant(variant),
        trackABConversion,
        fetchExperiment,
        user,
        experiment,
      }}
    >
      {children}
    </ABTestUndoCancelContext.Provider>
  );
};

export default ABTestUndoCancelProvider;
